<template>
  <div
    ref="table-invoicing-container"
    class="relative flex-1 overflow-auto rounded-md border border-slate-300 dark:border-slate-300 dark:bg-slate-700 dark:text-gray-300"
  >
    <table
      v-if="invoices.length"
      class="dashboard-table"
      style="border-spacing: 0"
    >
      <thead>
        <draggable
          id="dragger"
          v-model="localColumns"
          item-key="name"
          tag="tr"
          :component-data="{ as: 'transition-group' }"
          :revert-on-spill="false"
          :draggable="'.draggable-cell'"
          :move="handleMove"
          @update="handleDrag"
        >
          <template #item="{ index }">
            <DashBoardHeadColumnComponent
              v-model:column="localColumns[index]"
              v-model:sort="localSort"
              v-model:cell-width="cellWidth"
              v-model:require-save-dashboard="localRequireSaveDashboard"
              :index="index"
              :is-last="columns.length === index + 1"
              :resize-able="columns.length > 1"
              :sum-able="true"
              :search-able="true"
              :available-column-fields="availableFields"
              @delete:column="handleDeleteColumns"
              @update:dashboard="updateDashboard"
            />
          </template>
        </draggable>
      </thead>
      <tbody>
        <InvoiceRowComponent
          v-for="(invoice, indexInvoice) in localInvoicesPaginated"
          :key="invoice.id"
          :invoice="invoice"
          :columns="columns"
          :cell-width="cellWidthComputed"
          :index-invoice="indexInvoice"
          :is-last="indexInvoice === invoices.length - 1 && !hasSumAbleColumns"
        />
        <tr v-if="columns && countInvoices > page * pageSize">
          <td class="pagination" :colspan="columns.length">
            <div
              class="flex flex-row gap-3"
              :style="{ width: `${visibleWidthTable}px` }"
            >
              <button
                class="bg-secondary text-contrast-secondary flex w-full cursor-pointer flex-row items-center justify-center gap-1 px-1 py-2 text-xs font-semibold"
                @click="page++"
              >
                <span>{{ $t('global.display_more') }}</span>
                <span>{{ page * pageSize }}/{{ countInvoices }}</span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="hasSumAbleColumns" class="sticky bottom-0 z-30">
        <InvoiceRowComponent
          v-if="true"
          :invoice="invoicesSums"
          :columns="sumColumns"
          :cell-width="cellWidthComputed"
          :index-invoice="1"
          :is-last="true"
          :is-sum="true"
          :force-bold="true"
        />
      </tfoot>
    </table>
    <div
      v-else-if="loading"
      class="flex h-full w-full items-center justify-center"
    >
      <LoadingComponent size="small" />
    </div>
    <div v-else class="flex h-full w-full items-center justify-center">
      <h3
        class="border-primary bg-primary-100 rounded border px-2 py-0.5 text-base font-bold"
      >
        {{ $t('global.no_results') }}
      </h3>
    </div>
  </div>
</template>
<script>
import DashBoardHeadColumnComponent from '@c/Dashboard/Content/Dossier/components/Head/DashBoardHeadColumnComponent'
import InvoiceRowComponent from '@c/Dashboard/Content/Invoicing/components/Body/InvoiceRowComponent'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'InvoicingTable',
  components: {
    LoadingComponent,
    InvoiceRowComponent,
    DashBoardHeadColumnComponent,
    draggable
  },
  props: {
    invoices: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: false,
      default: () => {}
    },
    requireSaveDashboard: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:columns',
    'update:require-save-dashboard',
    'update:sort',
    'update:dashboard',
    'fetch-invoices',
    'refresh-click'
  ],
  data() {
    const cellWidth = {}
    for (let key in this.columns) {
      cellWidth[key] = this.columns[key].visual?.width || 'auto'
    }
    return {
      cellWidth,
      pageSize: 100,
      page: 1,
      currentSort: '',
      localInvoices: [...this.invoices],
      visibleWidthTable: 0
    }
  },
  computed: {
    ...mapGetters({
      column_types: 'metadata/getColumnTypesInvoicingDashboard'
    }),
    countInvoices() {
      return this.localInvoices.length
    },
    localInvoicesPaginated() {
      return this.localInvoices.slice(0, this.pageSize * this.page)
    },
    cellWidthComputed() {
      let result = Object.values(this.cellWidth).map((column) =>
        column == 'auto' ? 'auto' : `${Math.round(column)}px`
      )
      return result
    },
    availableFields() {
      return this.column_types || []
    },
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    },
    localColumns: {
      get() {
        return this.columns ?? []
      },
      set(value) {
        this.$emit('update:columns', value)
      }
    },
    localSort: {
      get() {
        return this.sort
      },
      set(value) {
        this.$emit('update:sort', value)
      }
    },
    hasSumAbleColumns() {
      return !!this.localColumns?.filter((clm) => clm.sum_able).length
    },
    sumColumns() {
      return this.localColumns.map((clm, k) => {
        return {
          ...clm,
          type: clm.sum_able ? clm.type : 'text'
        }
      })
    },
    invoicesSums() {
      const data = {
        id: 'total-sum'
      }

      for (let k in this.localColumns) {
        let column = this.localColumns[k],
          sum = k < 1 ? this.$t('global.total') : null

        if (column.sum_able && k > 0) {
          sum = this.localInvoices
            // Return the column value
            .map((inv) => inv[column.field])
            // Get value with number
            .filter((count) => count?.toString()?.match(/[0-9.,]+/))
            // Parse number to float
            .map((count) => parseFloat(count.toString().replace(',', '.')))

          if (sum.length) {
            // Get sum of all value and fixe float to 2 numbers
            sum = parseFloat(sum.reduce((a, b) => a + b, 0).toFixed(2))
          } else {
            sum = null
          }
        }

        data[column.field] = sum
      }

      return data
    },
    invoicesSumsOld() {
      return this.localColumns.map((clm, k) => {
        let sum = !k ? this.$t('global.total') : null

        if (clm.sum_able && k) {
          sum = this.localInvoices
            // Return the column value
            .map((inv) => inv[clm.field])
            // Get value with number
            .filter((count) => count?.toString()?.match(/[0-9.,]+/))
            // Parse number to float
            .map((count) => parseFloat(count.toString().replace(',', '.')))

          if (sum.length) {
            // Get sum of all value and fixe float to 2 numbers
            sum = parseFloat(sum.reduce((a, b) => a + b, 0).toFixed(2))
          } else {
            sum = null
          }
        }

        return {
          id: k + 1,
          value: sum,
          [clm.field]: sum,
          column: clm
        }
      })
    }
  },
  watch: {
    invoices: {
      immediate: true,
      handler(newInvoices) {
        this.localInvoices = [...newInvoices]
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.tableResized)
    this.tableResized()
  },
  unmounted() {
    window.removeEventListener('resize', this.tableResized)
  },
  methods: {
    tableResized() {
      this.visibleWidthTable =
        this.$refs['table-invoicing-container'].offsetWidth
    },
    handleDrag(e) {
      if (!this.localRequireSaveDashboard) {
        this.localRequireSaveDashboard = true
      }
    },
    handleMove(e) {
      return e.related.previousSibling !== null
    },
    localCellWidth(index) {
      if (this.cellWidth[index] > 0) {
        return `${this.cellWidth[index]}px`
      }

      return 'auto'
    },
    handleDeleteColumns(idx) {
      this.localColumns.splice(idx, 1)
      this.updateDashboard(false)
    },
    updateDashboard(value) {
      this.$emit('update:dashboard', value)
    },
    handleSortColumn({ column, direction }) {
      if (direction === '-') {
        this.$emit('refresh-click')
        return
      }
      this.currentSort = column
      let sortedModels = [...this.localInvoices]
      sortedModels = sortedModels.sort((a, b) => {
        const fa =
          a[column]?.value?.toLowerCase() ||
          a[column]?.id?.toLowerCase() ||
          a[column]?.toLowerCase()
        const fb =
          b[column]?.value?.toLowerCase() ||
          b[column]?.id?.toLowerCase() ||
          b[column]?.toLowerCase()

        if (fa < fb) {
          return direction === 'up' ? -1 : 1
        }
        if (fa > fb) {
          return direction === 'up' ? 1 : -1
        }
        return 0
      })
      this.localInvoices = sortedModels
    }
  }
}
</script>
